"use client"

import { BlockchainsMoleculeReatom } from "@/app/blockchains/blockchain-molecule.reatom"
import { Blockchain } from "@anaconda/core/blockchain/types"
import type { EthereumBlockchainConfig } from "@anaconda/core/blockchain/types"
import {
  type BlockchainBalanceSDK,
  createBlockchainBalanceSDK,
} from "@rarible_int/anaconda_sdk/ethereum"
import { atom } from "@reatom/core"
import { useWrap } from "@reatom/react"
import { molecule } from "bunshi"
import { useMolecule } from "bunshi/react"
import { useEffect, useState } from "react"
import { http, type PublicClient, createPublicClient } from "viem"

export type { BlockchainBalanceSDK }

export const BlockchainBalanceSdkProviderMoleculeReatom = molecule(() => {
  // Atom to hold the balance SDK instance (null until initialized)
  const balanceSDKAtom = atom<BlockchainBalanceSDK | null>(
    null as BlockchainBalanceSDK | null,
    "balanceSDKAtom",
  )

  return {
    balanceSDKAtom,
  }
})

/**
 * Hook to initialize blockchain balance SDK
 * IMPORTANT: Component using this hook must be wrapped with reatomComponent
 *
 * Pattern:
 * - Creates map of public clients (one per blockchain)
 * - Creates SDK object with methods that resolve publicClient internally
 * - Syncs SDK to Reatom atom for access by other components
 */
export function useBlockchainBalanceSDKProviderReatom() {
  const { blockchainsList, blockchainsMap } = useMolecule(
    BlockchainsMoleculeReatom,
  )
  const { balanceSDKAtom } = useMolecule(
    BlockchainBalanceSdkProviderMoleculeReatom,
  )

  // Call atom directly - requires parent component wrapped with reatomComponent
  const blockchains = blockchainsList()

  // Local React state for SDK instance
  const [balanceSDK, setBalanceSDK] = useState<BlockchainBalanceSDK | null>(
    null,
  )

  // Initialize SDK when blockchains list is available
  useEffect(() => {
    if (blockchains.length === 0) return

    try {
      const publicClientsMap: Record<Blockchain, PublicClient | null> =
        {} as Record<Blockchain, PublicClient | null>
      const solanaRpcUrlsMap: Partial<Record<Blockchain, string | null>> = {}

      for (const config of blockchains) {
        const rpcUrl =
          config.rpcUrls.find((url) => !url.isPublic)?.url ||
          config.rpcUrls[0]?.url

        if (config.isEvm) {
          if (!rpcUrl) {
            publicClientsMap[config.blockchain] = null
            continue
          }

          const evmConfig = config as EthereumBlockchainConfig
          publicClientsMap[evmConfig.blockchain] = createPublicClient({
            transport: http(rpcUrl),
          })
          continue
        }

        publicClientsMap[config.blockchain] = null

        if (
          config.blockchain === Blockchain.SOLANA ||
          config.blockchain === Blockchain.ECLIPSE
        ) {
          solanaRpcUrlsMap[config.blockchain] = rpcUrl ?? null
        }
      }

      const sdk = createBlockchainBalanceSDK({
        publicClientsMap,
        solanaRpcUrlsMap,
      })
      setBalanceSDK(sdk)
    } catch (error) {
      console.error("Failed to initialize blockchain balance SDK:", error)
    }
  }, [blockchains])

  // Sync React state to Reatom atom for access by other components
  const syncBalanceSDK = useWrap(() => {
    balanceSDKAtom.set(balanceSDK)
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    syncBalanceSDK()
  }, [balanceSDK, syncBalanceSDK])

  return { balanceSDK }
}
